import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistance, checkOpeningStatus } from '../utils/geoUtils';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import RatingStars from './RatingStars';
import { supabase } from '../supabase';
import { trackClick } from '../utils/analytics';

const { FiMapPin, FiNavigation, FiStar, FiHeart, FiX, FiPhone, FiInfo, FiPlus } = FiIcons;
const { FaWaze, FaGoogle, FaApple } = FaIcons;

const ResultsList = ({ results, categoryLabel, bookmarks = [], onToggleBookmark, onSelectPlace }) => {
  const [ratings, setRatings] = useState({});
  const [navTarget, setNavTarget] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    setVisibleCount(10); // Reset pagination when new search results arrive
    if (results?.length > 0) fetchRatings();
  }, [results]);

  const fetchRatings = async () => {
    const ids = results.map(r => r.id);
    const { data, error } = await supabase
      .from('place_ratings_1740480000000')
      .select('place_id, rating')
      .in('place_id', ids);

    if (!error && data) {
      const agg = data.reduce((acc, curr) => {
        if (!acc[curr.place_id]) acc[curr.place_id] = { sum: 0, count: 0 };
        acc[curr.place_id].sum += curr.rating;
        acc[curr.place_id].count += 1;
        return acc;
      }, {});
      setRatings(agg);
    }
  };

  const handleLoadMore = () => {
    trackClick('Load More Results', 'Discovery');
    setVisibleCount(prev => Math.min(prev + 10, 30));
  };

  const handleNavigate = (type) => {
    if (!navTarget) return;
    trackClick(`Launch Navigator: ${type}`, 'NavigationChoice');
    const { lat, lng } = navTarget;
    let url = type === 'waze' ? `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes` : 
              type === 'apple' ? `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d` : 
              `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
    setNavTarget(null);
  };

  if (!results) return null;

  const visibleResults = results.slice(0, visibleCount);

  return (
    <div className="space-y-10 pb-24">
      <div className="flex items-center justify-between px-2">
        <h3 className="text-slate-950 font-black text-2xl tracking-tighter capitalize">
          {categoryLabel.replace('_', ' ')} <span className="text-indigo-600">Results</span>
        </h3>
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-3 py-1 rounded-full border border-slate-100">
          Showing {visibleResults.length} of {results.length}
        </span>
      </div>

      <div className="space-y-6">
        <AnimatePresence mode="popLayout">
          {visibleResults.map((item) => {
            const isBookmarked = bookmarks.some(b => b.id === item.id);
            const ratingData = ratings[item.id] || { sum: 0, count: 0 };
            const phone = item.tags.phone || item.tags['contact:phone'];
            
            return (
              <motion.div 
                key={item.id} 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[2.5rem] p-6 high-contrast-shadow border border-slate-100 relative group overflow-hidden"
              >
                <button 
                  onClick={() => { trackClick(`${isBookmarked ? 'Remove' : 'Add'} Bookmark`, 'ResultCard'); onToggleBookmark(item); }}
                  className={`absolute top-6 right-6 p-3 rounded-2xl transition-all shadow-lg z-10 ${isBookmarked ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-400 hover:text-indigo-600'}`}
                >
                  <SafeIcon icon={FiHeart} className={isBookmarked ? 'fill-white' : ''} />
                </button>

                <div className="mb-6 cursor-pointer" onClick={() => { trackClick('View Place Details', 'ResultCard'); onSelectPlace(item); }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[9px] font-black text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg uppercase tracking-wider border border-indigo-100">
                      {item.tagType}
                    </span>
                  </div>
                  <h4 className="font-black text-slate-950 text-xl leading-tight mb-2 pr-12">{item.name}</h4>
                  
                  {item.description && (
                    <p className="text-[11px] font-medium text-slate-500 line-clamp-2 mb-4 italic leading-relaxed">
                      "{item.description}"
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-4">
                    <RatingStars rating={ratingData.count > 0 ? ratingData.sum / ratingData.count : 0} count={ratingData.count} />
                    <div className="bg-indigo-600 text-white px-4 py-2 rounded-xl flex flex-col items-center">
                      <span className="text-lg font-black leading-none">{formatDistance(item.distance)}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => { trackClick('Call Business', 'ResultCard'); window.open(`tel:${phone}`, '_self'); }} disabled={!phone} className="flex flex-col items-center justify-center py-3 rounded-2xl border-2 border-slate-100 disabled:opacity-30">
                    <SafeIcon icon={FiPhone} className="text-lg mb-1" />
                    <span className="text-[8px] font-black uppercase tracking-widest">Call</span>
                  </button>
                  <button onClick={() => { trackClick('Start Routing', 'ResultCard'); setNavTarget({ lat: item.lat, lng: item.lng }); }} className="flex flex-col items-center justify-center py-3 rounded-2xl bg-slate-950 text-white">
                    <SafeIcon icon={FiNavigation} className="text-lg mb-1" />
                    <span className="text-[8px] font-black uppercase tracking-widest">Route</span>
                  </button>
                  <button onClick={() => { trackClick('View Full Details', 'ResultCard'); onSelectPlace(item); }} className="flex flex-col items-center justify-center py-3 rounded-2xl bg-indigo-50 text-indigo-600">
                    <SafeIcon icon={FiInfo} className="text-lg mb-1" />
                    <span className="text-[8px] font-black uppercase tracking-widest">Info</span>
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {visibleCount < results.length && (
        <div className="pt-4 px-2">
          <button 
            onClick={handleLoadMore}
            className="w-full bg-white border-2 border-slate-100 text-slate-950 py-5 rounded-[2rem] font-black uppercase tracking-[0.2em] text-[10px] flex items-center justify-center gap-3 hover:border-indigo-600 transition-all shadow-sm active:scale-[0.98]"
          >
            <SafeIcon icon={FiPlus} /> Load More Results
          </button>
        </div>
      )}

      <AnimatePresence>
        {navTarget && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center px-4 pb-10 sm:items-center sm:pb-0">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setNavTarget(null)} className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" />
            <motion.div initial={{ y: 100, opacity: 1 }} className="relative bg-white w-full max-w-sm rounded-[2.5rem] p-8 high-contrast-shadow">
              <div className="flex justify-between items-center mb-6">
                <h4 className="text-xl font-black text-slate-950 tracking-tight">Choose Navigator</h4>
                <button onClick={() => setNavTarget(null)} className="p-2 bg-slate-100 rounded-full"><SafeIcon icon={FiX} /></button>
              </div>
              <div className="space-y-4">
                <button onClick={() => handleNavigate('google')} className="w-full flex items-center gap-4 p-5 rounded-2xl bg-slate-50 border-2 border-slate-100 hover:border-indigo-600 transition-all">
                  <SafeIcon icon={FaGoogle} className="text-red-500" />
                  <span className="font-black text-slate-950 uppercase tracking-widest text-xs">Google Maps</span>
                </button>
                <button onClick={() => handleNavigate('apple')} className="w-full flex items-center gap-4 p-5 rounded-2xl bg-slate-50 border-2 border-slate-100 hover:border-slate-950 transition-all">
                  <SafeIcon icon={FaApple} className="text-slate-950" />
                  <span className="font-black text-slate-950 uppercase tracking-widest text-xs">Apple Maps</span>
                </button>
                <button onClick={() => handleNavigate('waze')} className="w-full flex items-center gap-4 p-5 rounded-2xl bg-slate-50 border-2 border-slate-100 hover:border-[#33CCFF] transition-all">
                  <SafeIcon icon={FaWaze} className="text-[#33CCFF]" />
                  <span className="font-black text-slate-950 uppercase tracking-widest text-xs">Waze Navigation</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResultsList;